/**
 * @file SharePoint email operations via REST API
 * Pure SharePoint email sending functionality
 */

/**
 * Default email templates for group requests
 */
export const DEFAULT_EMAIL_TEMPLATES = {
  newRequest: {
    subject: 'New Group Access Request - {groupName}',
    body: `
      <p>A new group access request has been submitted:</p>
      <ul>
        <li><strong>Requester:</strong> {requesterName} ({requesterEmail})</li>
        <li><strong>Group:</strong> {groupName}</li>
        <li><strong>Justification:</strong> {justification}</li>
        <li><strong>Request Date:</strong> {requestDate}</li>
      </ul>
      <p><a href="{approvalUrl}">Click here to review and approve this request</a></p>
    `,
  },

  approved: {
    subject: 'Group Access Request Approved - {groupName}',
    body: `
      <p>Your group access request has been approved:</p>
      <ul>
        <li><strong>Group:</strong> {groupName}</li>
        <li><strong>Approved by:</strong> {approverName}</li>
        <li><strong>Approval Date:</strong> {approvalDate}</li>
      </ul>
      {adminComments}
    `,
  },

  denied: {
    subject: 'Group Access Request Denied - {groupName}',
    body: `
      <p>Your group access request has been denied:</p>
      <ul>
        <li><strong>Group:</strong> {groupName}</li>
        <li><strong>Denied by:</strong> {approverName}</li>
        <li><strong>Denial Date:</strong> {approvalDate}</li>
      </ul>
      {adminComments}
    `,
  },
};

/**
 * Send email via SharePoint REST API
 * @param {object} spClient - SharePoint client instance
 * @param {object} emailOptions - Email configuration
 * @param {string|Array} emailOptions.to - Recipient email(s)
 * @param {string} emailOptions.subject - Email subject
 * @param {string} emailOptions.body - Email body (HTML)
 * @param {string|Array} [emailOptions.cc] - CC recipients
 * @param {string|Array} [emailOptions.bcc] - BCC recipients
 * @returns {Promise<void>}
 */
export async function sendEmail(spClient, emailOptions) {
  const { to, subject, body, cc = [], bcc = [] } = emailOptions;

  if (!to || (Array.isArray(to) && to.length === 0)) {
    throw new Error('Email requires at least one recipient');
  }

  if (!subject) {
    throw new Error('Email requires a subject');
  }

  if (!body) {
    throw new Error('Email requires a body');
  }

  // Ensure recipients are arrays
  const toArray = Array.isArray(to) ? to : [to];
  const ccArray = Array.isArray(cc) ? cc : cc ? [cc] : [];
  const bccArray = Array.isArray(bcc) ? bcc : bcc ? [bcc] : [];

  const emailData = {
    properties: {
      __metadata: { type: 'SP.Utilities.EmailProperties' },
      To: { results: toArray },
      CC: { results: ccArray },
      BCC: { results: bccArray },
      Subject: subject,
      Body: body,
    },
  };

  await spClient.request(
    'SP.Utilities.Utility.SendEmail',
    'POST',
    {
      headers: { 'Content-Type': 'application/json;odata=verbose' },
      body: JSON.stringify(emailData),
    },
    true
  );
}

/**
 * Replace template placeholders with actual values
 * @param {string} template - Template string with {placeholder} syntax
 * @param {object} variables - Key-value pairs for replacement
 * @returns {string} Processed template
 */
export function processTemplate(template, variables) {
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    return variables[key] !== undefined ? variables[key] : match;
  });
}

/**
 * Send new request notification to admin
 * @param {object} spClient - SharePoint client instance
 * @param {string} adminEmail - Admin email address
 * @param {object} requestData - Request information
 * @param {object} [templates] - Custom email templates
 * @param {string} [approvalUrl] - URL to approval interface
 * @returns {Promise<void>}
 */
export async function sendNewRequestNotification(
  spClient,
  adminEmail,
  requestData,
  templates = DEFAULT_EMAIL_TEMPLATES,
  approvalUrl = ''
) {
  const template = templates.newRequest || DEFAULT_EMAIL_TEMPLATES.newRequest;

  const variables = {
    requesterName: requestData.requesterName,
    requesterEmail: requestData.requesterEmail,
    groupName: requestData.requestedGroupName,
    justification: requestData.justification,
    requestDate: new Date(requestData.requestedAt).toLocaleDateString(),
    approvalUrl: approvalUrl,
  };

  await sendEmail(spClient, {
    to: adminEmail,
    subject: processTemplate(template.subject, variables),
    body: processTemplate(template.body, variables),
  });
}

/**
 * Send status change notification to requester
 * @param {object} spClient - SharePoint client instance
 * @param {object} requestData - Request information
 * @param {string} status - New status ('approved' or 'denied')
 * @param {object} [templates] - Custom email templates
 * @returns {Promise<void>}
 */
export async function sendStatusChangeNotification(
  spClient,
  requestData,
  status,
  templates = DEFAULT_EMAIL_TEMPLATES
) {
  const template = templates[status] || DEFAULT_EMAIL_TEMPLATES[status];
  if (!template) {
    throw new Error(`No email template found for status: ${status}`);
  }

  if (!requestData.processedByName) {
    throw new Error('Request data missing required processedByName field');
  }

  if (!requestData.processedAt) {
    throw new Error('Request data missing required processedAt field');
  }

  const variables = {
    groupName: requestData.requestedGroupName,
    approverName: requestData.processedByName,
    approvalDate: new Date(requestData.processedAt).toLocaleDateString(),
    adminComments: requestData.adminComments
      ? `<p><strong>Comments:</strong> ${requestData.adminComments}</p>`
      : '',
  };

  await sendEmail(spClient, {
    to: requestData.requesterEmail,
    subject: processTemplate(template.subject, variables),
    body: processTemplate(template.body, variables),
  });
}
